import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Bell, BellOff, Check, ChevronDown, ChevronUp, Loader2, MapPin, Plus, RefreshCw, Settings2, ShieldAlert, Sparkles, Truck, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { IngredientPicker } from "./IngredientPicker";
import { performStockAction, listStockQueue, retryQueuedEvent, discardQueuedEvent, STOCK_ACTION_QUEUED } from "@/lib/stockSync";
import { EventType } from "@/lib/events";
import { getDeviceId } from "@/lib/device";
import { cachedGet, cachedAtLabel } from "@/lib/refCache";
import { EVENT_SYNC_FLUSHED, drainNow } from "@/lib/eventSync";
import {
  R, fmtQty, pct, rangeToDates, RangePicker, StatTile, ExplainerBox, EmptyState, ErrorState, errMsg, fmtWhen, humanize,
  LOCATION_STATE_TONE, CONSUMPTION_TONE, CONSUMPTION_LABEL,
} from "./shared";

const KINDS = ["front_of_house", "kitchen", "bar", "storage", "prep", "other"];
const UNITS = ["kg", "g", "L", "ml", "pcs", "tots"];

/**
 * Session 39 — Inventory → Stock Locations.
 *
 * One place for everything location-related:
 *   Overview      — ingredient × location matrix (balance, state vs min/par, value)
 *   Fallback      — fallback / shortfall analytics + the event feed
 *   Replenishment — advisory top-up list + tasks; "Record transfer" completes one
 *   Transfer      — PHYSICAL move between two locations (the only thing that
 *                   writes a `transfer` ledger row)
 *   Settings      — locations CRUD, fallback on/off, notifications, default
 *                   receiving location, business-wide consumption order
 *
 * Vocabulary is deliberately strict everywhere in this file:
 *   FALLBACK  = an order automatically drew from a non-preferred location.
 *   TRANSFER  = a person physically moved stock and recorded it here.
 *   SHORTFALL = no location had stock; the sale still went through and the
 *               preferred balance went negative by that amount.
 */
export const StockLocationsPanel = ({ http, authHeaders, ingredients = [], sessionUser, sessionForEvents = null, canManage = true, canTransfer = true, onStockChanged, initialView }) => {
  const [view, setView] = useState(initialView || "overview");
  const [locData, setLocData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("all");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [query, setQuery] = useState("");
  const [configIng, setConfigIng] = useState(null); // ingredient row being configured
  const [cachedAt, setCachedAt] = useState(null); // Session 40.1 — balances shown from the offline cache

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, b] = await Promise.all([
        cachedGet(http, "/owner/stock-locations", { headers: authHeaders }),
        cachedGet(http, "/owner/inventory/stock-by-location", { headers: authHeaders }),
      ]);
      setLocData(a.data);
      setMatrix(b.data);
      setCachedAt(a.fromCache || b.fromCache ? (b.cachedAt || a.cachedAt || "unknown") : null);
    } catch (e) {
      // Offline: not an error — the Sync tab keeps working from IndexedDB.
      const offline = (typeof navigator !== "undefined" && navigator.onLine === false) || e?.code === "ERR_NETWORK";
      setError(offline ? "__offline__" : errMsg(e, "Unable to load stock locations."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders]);

  useEffect(() => {
    loadAll();
    const onOnline = () => loadAll();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const locations = useMemo(() => matrix?.locations || locData?.locations || [], [matrix?.locations, locData?.locations]);
  const activeLocations = useMemo(() => locations.filter((l) => l.active !== false), [locations]);
  const settings = locData?.settings || matrix?.settings || {};
  const locName = useCallback((id) => locations.find((l) => l.id === id)?.name || "—", [locations]);

  const rows = useMemo(() => {
    let out = matrix?.rows || [];
    // Session 66 — filter per DEPARTMENT (dept:<id>) with a legacy station fallback.
    if (category !== "all") out = out.filter((r) => (r.department_id ? `dept:${r.department_id}` : `legacy:${r.category === "bar" ? "bar" : "kitchen"}`) === category);
    if (onlyProblems) out = out.filter((r) => r.fallback_active || Object.values(r.locations || {}).some((c) => c.state !== "ok"));
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((r) => (r.name || "").toLowerCase().includes(q));
    return out;
  }, [matrix, category, onlyProblems, query]);

  const afterStockChange = async () => {
    await loadAll();
    if (onStockChanged) await onStockChanged();
  };

  return (
    <div className="space-y-4" data-testid="stock-locations-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
            {[
              { v: "overview", label: "Overview", icon: MapPin },
              { v: "fallback", label: "Fallback activity", icon: Sparkles },
              { v: "replenishment", label: "Replenishment", icon: Truck },
              ...(canTransfer ? [{ v: "transfer", label: "Record transfer", icon: ArrowRightLeft }] : []),
              // Session 41.5 — "Sync" moved to the header DeviceSyncPill.
              ...(canManage ? [{ v: "settings", label: "Locations & rules", icon: Settings2 }] : []),
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} className="min-h-9 rounded-lg px-3 text-xs" data-testid={`stock-locations-view-${t.v}`}>
                <t.icon className="mr-1.5 h-3.5 w-3.5" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={loadAll} disabled={loading} data-testid="stock-locations-refresh">
          {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}Refresh
        </Button>
      </div>

      {settings && settings.fallback_enabled === false ? (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="fallback-disabled-banner">
          <ShieldAlert className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          <span>Automatic fallback is <b>off</b>: orders only consume from the preferred location and anything it can't supply is booked as a shortfall.</span>
        </div>
      ) : null}

      {cachedAt ? (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="stock-locations-cached-banner">
          <WifiOff className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          <span>Showing <b>last known balances</b>{cachedAt !== "unknown" ? ` (as of ${cachedAtLabel(cachedAt)})` : ""} — you're offline. Transfers and other actions you record are saved on this device and applied when the connection returns.</span>
        </div>
      ) : null}
      {error === "__offline__" ? (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="stock-locations-offline-banner">
          <WifiOff className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          <span>You're <b>offline</b> — live balances can't be loaded right now. Anything you record is saved on this device (see the <b>Device sync</b> pill in the header) and applied when the connection returns.</span>
        </div>
      ) : error ? <ErrorState message={error} onRetry={loadAll} testId="stock-locations-error" /> : null}

      {view === "overview" ? (
        <OverviewView
          loading={loading} matrix={matrix} rows={rows} locations={locations} activeLocations={activeLocations}
          category={category} setCategory={setCategory} onlyProblems={onlyProblems} setOnlyProblems={setOnlyProblems} query={query} setQuery={setQuery}
          locName={locName} onConfigure={canManage ? (r) => setConfigIng(r) : null}
        />
      ) : null}
      {view === "fallback" ? <FallbackView http={http} authHeaders={authHeaders} locName={locName} settings={settings} onAcknowledged={onStockChanged} /> : null}
      {view === "replenishment" ? <ReplenishmentView http={http} authHeaders={authHeaders} canTransfer={canTransfer} canManage={canManage} sessionForEvents={sessionForEvents} onStockChanged={afterStockChange} /> : null}
      {view === "transfer" && canTransfer ? <TransferView http={http} authHeaders={authHeaders} ingredients={ingredients} activeLocations={activeLocations} matrix={matrix} sessionUser={sessionUser} sessionForEvents={sessionForEvents} onDone={afterStockChange} /> : null}
      {view === "settings" && canManage ? <SettingsView http={http} authHeaders={authHeaders} locData={locData} locations={locations} activeLocations={activeLocations} onChanged={loadAll} /> : null}

      <IngredientLocationDialog http={http} authHeaders={authHeaders} row={configIng} locations={locations} activeLocations={activeLocations} defaultPriority={matrix?.default_priority || []} onClose={() => setConfigIng(null)} onSaved={afterStockChange} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const stateLabel = (s) => ({ ok: "OK", low: "Low", out: "Out", negative: "Negative" }[s] || s);

const OverviewView = ({ loading, matrix, rows, locations, activeLocations, category, setCategory, onlyProblems, setOnlyProblems, query, setQuery, locName, onConfigure }) => {
  const [expanded, setExpanded] = useState({});
  if (loading && !matrix) return <Skeleton className="h-64 w-full rounded-2xl" />;
  if (!matrix) return null;
  const totals = matrix.location_totals || [];
  // Session 66 — department tabs derived from the matrix rows.
  const deptTabs = (() => {
    const seen = new Map();
    (matrix.rows || []).forEach((r) => {
      const key = r.department_id ? `dept:${r.department_id}` : `legacy:${r.category === "bar" ? "bar" : "kitchen"}`;
      if (!seen.has(key)) seen.set(key, { id: key, label: r.department_name || (key === "legacy:bar" ? "Bar" : "Kitchen"), dept: !!r.department_id });
    });
    const list = [...seen.values()].sort((a, b) => (a.dept === b.dept ? a.label.localeCompare(b.label) : a.dept ? -1 : 1));
    if (list.some((t) => t.dept)) list.forEach((t) => { if (!t.dept) t.label = `${t.label} (unassigned)`; });
    return list;
  })();
  return (
    <div className="space-y-3" data-testid="stock-locations-overview">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Stock value (all locations)" value={R(matrix.summary?.total_value)} hint={`${matrix.summary?.ingredients || 0} ingredients`} testId="overview-total-value" />
        {totals.filter((t) => t.active !== false).slice(0, 2).map((t) => (
          <StatTile key={t.id} label={t.name} value={R(t.value)} hint={`${t.out} out · ${t.low} low${t.negative ? ` · ${t.negative} negative` : ""}`} tone={t.negative ? "bad" : t.out || t.low ? "warn" : "default"} testId={`overview-location-${t.id}`} />
        ))}
        <StatTile label="Running on fallback" value={matrix.summary?.fallback_active || 0} hint={matrix.summary?.negative_cells ? `${matrix.summary.negative_cells} negative balance(s)` : "preferred location ran dry"} tone={matrix.summary?.fallback_active ? "warn" : "good"} testId="overview-fallback-active" />
      </div>
      {totals.filter((t) => t.active !== false).length > 2 ? (
        <div className="flex flex-wrap gap-1.5" data-testid="overview-more-locations">
          {totals.slice(2).filter((t) => t.active !== false).map((t) => <Badge key={t.id} variant="outline" className="rounded-full">{t.name}: {R(t.value)}</Badge>)}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="h-9 rounded-xl">
            {[{ id: "all", label: "All departments" }, ...deptTabs].map((k) => <TabsTrigger key={k.id} value={k.id} className="rounded-lg text-xs" data-testid={`overview-category-${k.id.replace(/^dept:/, "dept-").replace(/^legacy:/, "")}`}>{k.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Button type="button" size="sm" variant={onlyProblems ? "default" : "outline"} className="rounded-xl" onClick={() => setOnlyProblems((v) => !v)} data-testid="overview-only-problems">Only problems</Button>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search ingredient…" className="h-9 w-48 rounded-xl" data-testid="overview-search" />
      </div>

      <Card className="rounded-2xl border-border bg-card">
        <CardContent className="p-0 sm:p-2">
          {rows.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ingredient</TableHead>
                    {activeLocations.map((l) => <TableHead key={l.id} className="text-right">{l.name}</TableHead>)}
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Value</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const open = !!expanded[r.id];
                    return (
                      <React.Fragment key={r.id}>
                        <TableRow data-testid={`location-row-${r.id}`} className={r.active === false ? "opacity-60" : ""}>
                          <TableCell className="font-medium">
                            <span className="block truncate">{r.name}</span>
                            <span className="flex flex-wrap items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                              {r.department_name || r.category} · {r.unit}
                              {r.fallback_active ? <Badge variant="outline" className={`rounded-full text-[9px] normal-case ${CONSUMPTION_TONE.fallback}`} data-testid={`location-row-fallback-${r.id}`}>on fallback</Badge> : null}
                              {r.priority_is_override ? <Badge variant="outline" className="rounded-full text-[9px] normal-case">custom order</Badge> : null}
                            </span>
                          </TableCell>
                          {activeLocations.map((l, idx) => {
                            const c = r.locations?.[l.id] || { balance: 0, state: "out", levels: {} };
                            const isPreferred = r.priority?.[0] === l.id;
                            return (
                              <TableCell key={l.id} className="text-right" data-testid={`location-cell-${r.id}-${l.id}`}>
                                <span className={`inline-flex min-w-[64px] flex-col items-end rounded-lg border px-2 py-1 text-xs tabular-nums ${LOCATION_STATE_TONE[c.state] || LOCATION_STATE_TONE.ok}`} title={`${l.name}: ${stateLabel(c.state)}${c.levels?.min !== null && c.levels?.min !== undefined ? ` · min ${fmtQty(c.levels.min)}` : ""}${c.levels?.par !== null && c.levels?.par !== undefined ? ` · par ${fmtQty(c.levels.par)}` : ""}`}>
                                  <span className="font-semibold">{fmtQty(c.balance)}</span>
                                  <span className="text-[9px] uppercase opacity-80">{isPreferred ? "preferred" : `#${(r.priority || []).indexOf(l.id) + 1 || idx + 1}`}{c.state !== "ok" ? ` · ${stateLabel(c.state)}` : ""}</span>
                                </span>
                              </TableCell>
                            );
                          })}
                          <TableCell className={`text-right text-xs font-semibold tabular-nums ${r.state === "negative" ? "text-destructive" : r.state === "low" || r.state === "out" ? "text-amber-700 dark:text-amber-300" : ""}`} data-testid={`location-total-${r.id}`}>{fmtQty(r.total_stock)} {r.unit}</TableCell>
                          <TableCell className="hidden text-right text-xs tabular-nums sm:table-cell">{R(r.total_value)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {onConfigure ? <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg px-2" onClick={() => onConfigure(r)} title="Consumption order & min / par levels" data-testid={`location-configure-${r.id}`}><Settings2 className="h-4 w-4" /></Button> : null}
                              <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg px-2" onClick={() => setExpanded((m) => ({ ...m, [r.id]: !open }))} data-testid={`location-expand-${r.id}`}>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {open ? (
                          <TableRow className="bg-secondary/30" data-testid={`location-detail-${r.id}`}>
                            <TableCell colSpan={activeLocations.length + 4} className="text-xs text-muted-foreground">
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                <span>Consumption order: <b className="text-foreground">{(r.priority || []).map((id) => locName(id)).join(" → ") || "—"}</b>{r.priority_is_override ? " (ingredient override)" : " (business default)"}</span>
                                {locations.map((l) => {
                                  const lv = r.locations?.[l.id]?.levels || {};
                                  const has = [lv.min, lv.par, lv.max].some((v) => v !== null && v !== undefined);
                                  return has ? <span key={l.id}>{l.name}: {lv.min !== null && lv.min !== undefined ? `min ${fmtQty(lv.min)} ` : ""}{lv.par !== null && lv.par !== undefined ? `par ${fmtQty(lv.par)} ` : ""}{lv.max !== null && lv.max !== undefined ? `max ${fmtQty(lv.max)}` : ""}</span> : null;
                                })}
                                {r.cost_per_unit !== null && r.cost_per_unit !== undefined ? <span>Cost {R(r.cost_per_unit)}/{r.unit} (one cost for every location)</span> : <span>No cost set</span>}
                                {r.fallback_state?.active ? <span className="text-amber-700 dark:text-amber-300">Fallback since {fmtWhen(r.fallback_state.started_at)} · {r.fallback_state.events} order(s) · {fmtQty(r.fallback_state.fallback_qty)} {r.unit} drawn from other locations{Number(r.fallback_state.shortfall_qty) > 0 ? ` · ${fmtQty(r.fallback_state.shortfall_qty)} ${r.unit} shortfall` : ""}</span> : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-4"><EmptyState title="No ingredients match" body={onlyProblems ? "Nothing is low, out or negative in any location." : "Add ingredients under Inventory → Ingredients."} testId="overview-empty" /></div>
          )}
        </CardContent>
      </Card>
      <ExplainerBox title="How to read this" testId="overview-explainer">
        <p>Each cell is that ingredient's balance in that location. <b>Preferred</b> is where orders consume first; if it runs dry the next location in the consumption order is used automatically (<b>fallback</b>). A <b>negative</b> cell means sales went through with no stock anywhere (<b>shortfall</b>) — count and correct it in a stock take.</p>
        <p>Totals and values use one cost per ingredient regardless of location. Stock only moves between locations when someone records a <b>transfer</b>.</p>
      </ExplainerBox>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const FallbackView = ({ http, authHeaders, locName, settings, onAcknowledged }) => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [breakdown, setBreakdown] = useState("ingredient");
  const [acking, setAcking] = useState(false);

  const load = useCallback(async (rangeKey) => {
    setLoading(true);
    setError("");
    try {
      const days = { "1d": 1, yesterday: 2, "7d": 7, "30d": 30 }[rangeKey || range] || 7;
      const [a, b] = await Promise.all([
        http.get("/owner/inventory/fallback-activity", { headers: authHeaders, params: rangeToDates(rangeKey || range) }),
        http.get("/owner/inventory/stock-events", { headers: authHeaders, params: { days, limit: 100 } }),
      ]);
      setData(a.data);
      setEvents(b.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load fallback activity."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, range]);

  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range); }, []);

  const ackAll = async () => {
    setAcking(true);
    try {
      const r = await http.post("/owner/inventory/stock-events/acknowledge", { all: true }, { headers: authHeaders });
      toast.success(`${r.data?.acknowledged || 0} event(s) acknowledged`);
      await load(range);
      // Session 41.7 — clears the dashboard's fallback banner straight away.
      if (onAcknowledged) { try { await onAcknowledged(); } catch (_) { /* noop */ } }
    } catch (e) {
      toast.error(errMsg(e, "Couldn't acknowledge events."));
    } finally {
      setAcking(false);
    }
  };

  const groups = data ? ({ ingredient: data.by_ingredient, preferred: data.by_preferred_location, source: data.by_source_location, order: data.by_order, day: data.by_day }[breakdown] || []) : [];
  const t = data?.totals || {};
  return (
    <div className="min-w-0 max-w-full space-y-3" data-testid="fallback-view">
      <RangePicker value={range} onChange={(v) => { setRange(v); load(v); }} testIdPrefix="fallback" />
      {error ? <ErrorState message={error} onRetry={() => load(range)} testId="fallback-error" /> : null}
      {loading && !data ? <Skeleton className="h-40 w-full rounded-2xl" /> : data ? (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Fallback consumption" value={R(t.fallback_value)} hint={`${t.fallback_qty_rows || 0} deduction(s) from a non-preferred location`} tone={t.fallback_qty_rows ? "warn" : "good"} testId="fallback-total-value" />
            <StatTile label="Shortfall" value={R(t.shortfall_value)} hint={`${t.shortfall_qty_rows || 0} deduction(s) with no stock anywhere`} tone={t.shortfall_qty_rows ? "bad" : "good"} testId="fallback-shortfall-value" />
            <StatTile label="Orders affected" value={t.orders_affected || 0} hint={`${pct(t.fallback_share_pct)} of recipe deductions were fallback or shortfall`} testId="fallback-orders" />
            <StatTile label="Currently on fallback" value={data.active_fallbacks?.length || 0} hint={settings?.fallback_enabled === false ? "automatic fallback is OFF" : "preferred location empty right now"} tone={data.active_fallbacks?.length ? "warn" : "good"} testId="fallback-active-count" />
          </div>

          {data.active_fallbacks?.length ? (
            <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10" data-testid="fallback-active-list">
              <CardContent className="p-4">
                <p className="text-sm font-semibold">Ingredients running on fallback right now</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {data.active_fallbacks.map((a) => (
                    <Badge key={a.id} variant="outline" className="rounded-full bg-card" data-testid={`fallback-active-${a.id}`}>
                      {a.name}: {a.preferred_location_name} {fmtQty(a.preferred_balance)} · elsewhere {fmtQty(a.other_stock)} {a.unit}{Number(a.shortfall_qty) > 0 ? ` · shortfall ${fmtQty(a.shortfall_qty)}` : ""}
                    </Badge>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Move stock to the preferred location (Record transfer) or receive into it to end the episode.</p>
              </CardContent>
            </Card>
          ) : null}

          <Card className="rounded-2xl border-border bg-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold">Breakdown</p>
                <Tabs value={breakdown} onValueChange={setBreakdown} className="max-w-full">
                  <TabsList className="flex h-auto max-w-full flex-wrap justify-start gap-0.5 rounded-lg">
                    {[["ingredient", "Ingredient"], ["preferred", "Preferred location"], ["source", "Drawn from"], ["order", "Order"], ["day", "Day"]].map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-md text-xs" data-testid={`fallback-breakdown-${k}`}>{l}</TabsTrigger>)}
                  </TabsList>
                </Tabs>
              </div>
              {groups.length ? (
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{breakdown === "day" ? "Date" : humanize(breakdown)}</TableHead>
                        <TableHead className="text-right">Fallback qty</TableHead>
                        <TableHead className="text-right">Fallback value</TableHead>
                        <TableHead className="text-right">Shortfall qty</TableHead>
                        <TableHead className="text-right">Shortfall value</TableHead>
                        <TableHead className="hidden text-right sm:table-cell">Rows</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map((g) => (
                        <TableRow key={g.key} data-testid={`fallback-group-${g.key}`}>
                          <TableCell className="text-xs font-medium">{g.label}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{Number(g.fallback_qty) > 0 ? `${fmtQty(g.fallback_qty)} ${g.unit || ""}` : "—"}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{Number(g.fallback_value) > 0 ? R(g.fallback_value) : "—"}</TableCell>
                          <TableCell className={`text-right text-xs tabular-nums ${Number(g.shortfall_qty) > 0 ? "text-destructive" : ""}`}>{Number(g.shortfall_qty) > 0 ? `${fmtQty(g.shortfall_qty)} ${g.unit || ""}` : "—"}</TableCell>
                          <TableCell className={`text-right text-xs tabular-nums ${Number(g.shortfall_value) > 0 ? "text-destructive" : ""}`}>{Number(g.shortfall_value) > 0 ? R(g.shortfall_value) : "—"}</TableCell>
                          <TableCell className="hidden text-right text-xs tabular-nums sm:table-cell">{g.events}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="mt-3"><EmptyState title="No fallback or shortfall in this period" body="Every order was served from its preferred location." testId="fallback-empty" /></div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card" data-testid="fallback-events">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">Event feed</p>
                  <p className="text-xs text-muted-foreground">{events?.counts?.unacknowledged || 0} unacknowledged · {events?.counts?.notify || 0} worth a look (first fallback of an episode, or any shortfall)</p>
                </div>
                <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={ackAll} disabled={acking || !(events?.counts?.unacknowledged)} data-testid="fallback-ack-all">
                  {acking ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}Acknowledge all
                </Button>
              </div>
              {events?.events?.length ? (
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Ingredient</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="hidden md:table-cell">Detail</TableHead>
                        <TableHead className="hidden sm:table-cell">Order</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.events.map((e) => (
                        <TableRow key={e.id} className={e.acknowledged ? "opacity-60" : ""} data-testid={`stock-event-${e.id}`}>
                          <TableCell className="text-xs tabular-nums">{fmtWhen(e.created_at)}</TableCell>
                          <TableCell><Badge variant="outline" className={`rounded-full text-[10px] ${CONSUMPTION_TONE[e.type] || ""}`}>{CONSUMPTION_LABEL[e.type] || e.type}{e.type === "fallback" && !e.first_in_episode ? " (repeat)" : ""}</Badge></TableCell>
                          <TableCell className="text-xs font-medium">{e.ingredient_name}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{e.quantity_label || `${fmtQty(e.quantity)} ${e.unit || ""}`}{e.value !== null && e.value !== undefined ? <span className="block text-[10px] text-muted-foreground">{R(e.value)}</span> : null}</TableCell>
                          <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                            {e.type === "fallback" ? `${e.preferred_location_name} ran dry → drawn from ${(e.from_locations || []).map((f) => `${f.name} (${fmtQty(f.quantity)})`).join(", ")}` : `No stock in any location · ${e.preferred_location_name} now ${fmtQty(e.preferred_balance_after)}`}
                          </TableCell>
                          <TableCell className="hidden text-xs sm:table-cell">{e.order_number || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : <div className="mt-3"><EmptyState title="No events" testId="fallback-events-empty" /></div>}
            </CardContent>
          </Card>
          {data.method ? <p className="text-[11px] text-muted-foreground" data-testid="fallback-method">Method: {data.method}</p> : null}
        </>
      ) : null}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const ReplenishmentView = ({ http, authHeaders, canTransfer, canManage, sessionForEvents, onStockChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // recommendation to transfer

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await http.get("/owner/inventory/replenishment", { headers: authHeaders });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load replenishment suggestions."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders]);
  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const createTask = async (rec) => {
    setBusy(`task-${rec.ingredient_id}-${rec.to_location_id}`);
    try {
      await http.post("/owner/inventory/replenishment/tasks", { ingredient_id: rec.ingredient_id, to_location_id: rec.to_location_id, from_location_id: rec.from_location_id || null, quantity: rec.suggested_qty, reason: rec.reason }, { headers: authHeaders });
      toast.success("Task created");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't create task."));
    } finally {
      setBusy("");
    }
  };
  const setTaskStatus = async (task, status) => {
    setBusy(`status-${task.id}`);
    try {
      await http.patch(`/owner/inventory/replenishment/tasks/${task.id}`, { status }, { headers: authHeaders });
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't update task."));
    } finally {
      setBusy("");
    }
  };
  const doTransfer = async () => {
    if (!confirm) return;
    const qty = Number(confirm.qty);
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Enter a quantity greater than zero."); return; }
    setBusy("transfer");
    try {
      const body = {
        from_location_id: confirm.rec.from_location_id, to_location_id: confirm.rec.to_location_id,
        lines: [{ ingredient_id: confirm.rec.ingredient_id, quantity: qty }], notes: "Replenishment", replenishment_task_id: confirm.rec.open_task_id || confirm.rec.task_id || null,
      };
      // Session 40 — offline-safe (queued once with a change_id; never a fallback).
      const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/transfer", body, eventType: EventType.STOCK_TRANSFERRED, session: sessionForEvents || null });
      if (res.status === "queued") {
        toast.info(`Transfer saved on this device — ${fmtQty(qty)} ${confirm.rec.unit || ""} ${confirm.rec.name} will sync when the connection returns.`);
        setConfirm(null);
        return;
      }
      const mv = res.data?.moved?.[0];
      toast.success(mv ? `Moved ${fmtQty(mv.quantity)} ${mv.unit} ${mv.from.name} → ${mv.to.name}` : "Transfer recorded");
      setConfirm(null);
      await load();
      if (onStockChanged) await onStockChanged();
    } catch (e) {
      toast.error(errMsg(e, "Transfer failed."));
    } finally {
      setBusy("");
    }
  };

  const recs = data?.recommendations || [];
  const tasks = data?.open_tasks || [];
  return (
    <div className="space-y-3" data-testid="replenishment-view">
      {error ? <ErrorState message={error} onRetry={load} testId="replenishment-error" /> : null}
      {loading && !data ? <Skeleton className="h-40 w-full rounded-2xl" /> : data ? (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <StatTile label="Suggestions" value={data.summary?.count || 0} hint={`${data.summary?.transfers || 0} transfer(s) · ${data.summary?.purchases || 0} purchase(s)`} tone={data.summary?.count ? "warn" : "good"} testId="replenishment-count" />
            <StatTile label="Value to move" value={R(data.summary?.value)} hint="at current cost per unit" testId="replenishment-value" />
            <StatTile label="Open tasks" value={tasks.length} hint="completed automatically when the transfer is recorded" testId="replenishment-open-tasks" />
          </div>
          <Card className="rounded-2xl border-border bg-card">
            <CardContent className="p-4">
              <p className="text-sm font-semibold">What to move (or buy) now</p>
              {recs.length ? (
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ingredient</TableHead>
                        <TableHead>Where</TableHead>
                        <TableHead className="text-right">Now</TableHead>
                        <TableHead className="text-right">Suggested</TableHead>
                        <TableHead className="hidden md:table-cell">Why</TableHead>
                        <TableHead className="text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recs.map((r) => {
                        const key = `${r.ingredient_id}-${r.to_location_id}`;
                        return (
                          <TableRow key={key} data-testid={`replenishment-row-${key}`}>
                            <TableCell className="text-xs font-medium">
                              <span className="block">{r.name}</span>
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.category}{r.fallback_active ? " · on fallback" : ""}</span>
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.action === "transfer" ? <span className="inline-flex items-center gap-1">{r.from_location_name} <ArrowRightLeft className="h-3 w-3" /> <b>{r.to_location_name}</b></span> : <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" /> buy for <b>{r.to_location_name}</b></span>}
                              {r.action === "transfer" ? <span className="block text-[10px] text-muted-foreground">{fmtQty(r.source_available)} {r.unit} available at source</span> : null}
                            </TableCell>
                            <TableCell className={`text-right text-xs tabular-nums ${r.balance < 0 ? "text-destructive" : ""}`}>{fmtQty(r.balance)} {r.unit}</TableCell>
                            <TableCell className="text-right text-xs font-semibold tabular-nums">{fmtQty(r.suggested_qty)} {r.unit}{r.value !== null && r.value !== undefined ? <span className="block text-[10px] font-normal text-muted-foreground">{R(r.value)}</span> : null}</TableCell>
                            <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{r.reason}{r.avg_daily_usage ? ` · ~${fmtQty(r.avg_daily_usage)} ${r.unit}/day` : ""}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-wrap justify-end gap-1">
                                {r.action === "transfer" && canTransfer ? <Button type="button" size="sm" className="h-8 rounded-lg text-xs" onClick={() => setConfirm({ rec: r, qty: r.suggested_qty })} data-testid={`replenishment-transfer-${key}`}>Record transfer</Button> : null}
                                {(canManage || canTransfer) && !r.open_task_id ? <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg text-xs" disabled={busy === `task-${key}`} onClick={() => createTask(r)} data-testid={`replenishment-task-${key}`}>{busy === `task-${key}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create task"}</Button> : r.open_task_id ? <Badge variant="outline" className="rounded-full text-[10px]">task open</Badge> : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : <div className="mt-3"><EmptyState title="Nothing to replenish" body="Every preferred location has stock and no location is under its minimum." testId="replenishment-empty" /></div>}
            </CardContent>
          </Card>
          {tasks.length ? (
            <Card className="rounded-2xl border-border bg-card" data-testid="replenishment-tasks">
              <CardContent className="p-4">
                <p className="text-sm font-semibold">Open tasks</p>
                <div className="mt-2 space-y-1.5">
                  {tasks.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs" data-testid={`replenishment-task-row-${t.id}`}>
                      <span><b>{t.ingredient_name}</b> · {fmtQty(t.quantity)} {t.unit} {t.from_location_name ? `${t.from_location_name} → ` : ""}{t.to_location_name}{t.reason ? <span className="text-muted-foreground"> · {t.reason}</span> : null}<span className="block text-[10px] text-muted-foreground">{fmtWhen(t.created_at)} · {t.created_by}</span></span>
                      <div className="flex gap-1">
                        {canTransfer && t.from_location_id ? <Button type="button" size="sm" className="h-7 rounded-lg text-xs" onClick={() => setConfirm({ rec: { ingredient_id: t.ingredient_id, name: t.ingredient_name, unit: t.unit, from_location_id: t.from_location_id, from_location_name: t.from_location_name, to_location_id: t.to_location_id, to_location_name: t.to_location_name, task_id: t.id }, qty: t.quantity })} data-testid={`task-transfer-${t.id}`}>Record transfer</Button> : null}
                        <Button type="button" size="sm" variant="ghost" className="h-7 rounded-lg text-xs" disabled={busy === `status-${t.id}`} onClick={() => setTaskStatus(t, "dismissed")} data-testid={`task-dismiss-${t.id}`}>Dismiss</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
          {data.method ? <p className="text-[11px] text-muted-foreground">Method: {data.method}</p> : null}
        </>
      ) : null}

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="replenishment-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Record a physical transfer</DialogTitle>
            <DialogDescription>Only record this once the stock has actually been carried. {confirm?.rec?.from_location_name} → {confirm?.rec?.to_location_name}.</DialogDescription>
          </DialogHeader>
          {confirm ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">{confirm.rec.name}</p>
              <div className="space-y-1.5">
                <Label className="text-xs">Quantity ({confirm.rec.unit})</Label>
                <Input type="number" min="0" step="any" value={confirm.qty} onChange={(e) => setConfirm((c) => ({ ...c, qty: e.target.value }))} className="h-11 rounded-xl" data-testid="replenishment-transfer-qty" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setConfirm(null)} data-testid="replenishment-transfer-cancel">Cancel</Button>
                <Button type="button" className="rounded-xl" onClick={doTransfer} disabled={busy === "transfer"} data-testid="replenishment-transfer-confirm">{busy === "transfer" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-1.5 h-4 w-4" />}Record transfer</Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const TransferView = ({ http, authHeaders, ingredients, activeLocations, matrix, sessionUser, sessionForEvents, onDone }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [lines, setLines] = useState([{ _k: "k0", ingredient_id: "", quantity: "", unit: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!from && activeLocations.length > 1) { setFrom(activeLocations[1].id); setTo(activeLocations[0].id); }
    else if (!from && activeLocations.length === 1) { setFrom(activeLocations[0].id); }
  }, [activeLocations, from]);

  const balanceAt = (ingId, locId) => matrix?.rows?.find((r) => r.id === ingId)?.locations?.[locId]?.balance;
  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!from || !to) { toast.error("Pick both locations."); return; }
    if (from === to) { toast.error("From and to must differ."); return; }
    const clean = lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0).map((l) => ({ ingredient_id: l.ingredient_id, quantity: Number(l.quantity), unit: l.unit || null }));
    if (!clean.length) { toast.error("Add at least one line with a quantity."); return; }
    setSaving(true);
    try {
      const body = { from_location_id: from, to_location_id: to, lines: clean, notes: notes.trim() || null };
      // Session 40 — offline-safe: queued with its own change_id when the
      // connection is down; the server records exactly one TRANSFER on replay.
      const res = await performStockAction({ http, authHeaders, path: "/owner/inventory/transfer", body, eventType: EventType.STOCK_TRANSFERRED, session: sessionForEvents || null });
      if (res.status === "queued") {
        setResult({ queued: true, change_id: res.change_id, lines: clean.map((l) => ({ ...l, name: ingredients.find((x) => x.id === l.ingredient_id)?.name })), from_name: activeLocations.find((l) => l.id === from)?.name, to_name: activeLocations.find((l) => l.id === to)?.name });
        toast.info("Transfer saved on this device — it will sync when the connection returns.");
        setLines([{ _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, ingredient_id: "", quantity: "", unit: "" }]);
        setNotes("");
        return;
      }
      setResult(res.data);
      toast.success(`${res.data?.moved?.length || 0} line(s) moved`);
      setLines([{ _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, ingredient_id: "", quantity: "", unit: "" }]);
      setNotes("");
      if (onDone) await onDone();
    } catch (e2) {
      toast.error(errMsg(e2, "Transfer failed."));
    } finally {
      setSaving(false);
    }
  };
  const swap = () => { const f = from; setFrom(to); setTo(f); };

  return (
    <div className="grid gap-3 lg:grid-cols-[1.1fr_1fr]" data-testid="transfer-view">
      <Card className="rounded-2xl border-border bg-card">
        <CardContent className="p-4">
          <p className="text-sm font-semibold">Record a physical transfer</p>
          <p className="text-xs text-muted-foreground">Someone carried stock from one location to another. This is the <b>only</b> action that writes a transfer to the ledger — automatic fallback never does.</p>
          <form className="mt-3 space-y-3" onSubmit={submit}>
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">From</Label>
                <Select value={from} onValueChange={setFrom}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="transfer-from"><SelectValue placeholder="Location" /></SelectTrigger>
                  <SelectContent>{activeLocations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`transfer-from-${l.id}`}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="button" variant="outline" size="icon" className="h-11 w-11 rounded-xl" onClick={swap} title="Swap" data-testid="transfer-swap"><ArrowRightLeft className="h-4 w-4" /></Button>
              <div className="space-y-1.5">
                <Label className="text-xs">To</Label>
                <Select value={to} onValueChange={setTo}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="transfer-to"><SelectValue placeholder="Location" /></SelectTrigger>
                  <SelectContent>{activeLocations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`transfer-to-${l.id}`}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => {
                const ing = ingredients.find((x) => x.id === l.ingredient_id);
                const avail = ing && from ? balanceAt(ing.id, from) : undefined;
                return (
                  <div key={l._k || `line-${i}`} className="grid gap-2 sm:grid-cols-[1fr_120px_100px_auto]" data-testid={`transfer-line-${i}`}>
                    <IngredientPicker ingredients={ingredients} value={l.ingredient_id} onChange={(id) => { const ig = ingredients.find((x) => x.id === id); setLine(i, { ingredient_id: id, unit: ig?.unit || "" }); }} testId={`transfer-line-ingredient-${i}`} />
                    <div>
                      <Input type="number" min="0" step="any" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-11 rounded-xl" data-testid={`transfer-line-qty-${i}`} />
                      {avail !== undefined ? <span className="text-[10px] text-muted-foreground">{fmtQty(avail)} {ing?.unit} available</span> : null}
                    </div>
                    <Select value={l.unit || ing?.unit || "pcs"} onValueChange={(v) => setLine(i, { unit: v })}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid={`transfer-line-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="sm" className="h-11 rounded-xl" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))} data-testid={`transfer-line-remove-${i}`}>Remove</Button>
                  </div>
                );
              })}
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setLines((ls) => [...ls, { _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, ingredient_id: "", quantity: "", unit: "" }])} data-testid="transfer-add-line"><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl" data-testid="transfer-notes" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Recorded by {sessionUser?.full_name || "current user"}</span>
              <Button type="submit" className="rounded-xl" disabled={saving} data-testid="transfer-submit">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-1.5 h-4 w-4" />}Record transfer</Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="space-y-3">
        {result?.queued ? (
          <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10" data-testid="transfer-queued">
            <CardContent className="p-4">
              <p className="flex items-center gap-2 text-sm font-semibold"><WifiOff className="h-4 w-4" /> Saved on this device — waiting for connection</p>
              <ul className="mt-2 space-y-1 text-xs">
                {(result.lines || []).map((l, i) => <li key={i}><b>{l.name || l.ingredient_id}</b>: {fmtQty(l.quantity)} {l.unit || ""} {result.from_name} → {result.to_name}</li>)}
              </ul>
              <p className="mt-2 text-[11px] text-muted-foreground">Event {result.change_id}. It is stored in this device's queue, survives a restart, and will be applied exactly once when the server is reachable. Track it via the <b>Device sync</b> pill in the header.</p>
            </CardContent>
          </Card>
        ) : result ? (
          <Card className="rounded-2xl border-emerald-500/40 bg-emerald-500/10" data-testid="transfer-result">
            <CardContent className="p-4">
              <p className="text-sm font-semibold">Moved</p>
              <ul className="mt-2 space-y-1 text-xs">
                {(result.moved || []).map((m) => <li key={m.ingredient_id} data-testid={`transfer-moved-${m.ingredient_id}`}><b>{m.name}</b>: {fmtQty(m.quantity)} {m.unit} {m.from.name} ({fmtQty(m.from.balance)} left) → {m.to.name} (now {fmtQty(m.to.balance)}){m.requested > m.quantity ? <span className="text-amber-700 dark:text-amber-300"> · capped at what was available</span> : null}</li>)}
              </ul>
              {result.skipped?.length ? <p className="mt-2 text-xs text-muted-foreground">Skipped: {result.skipped.map((s) => `${s.name || s.ingredient_id} (${s.reason})`).join(", ")}</p> : null}
            </CardContent>
          </Card>
        ) : null}
        <ExplainerBox title="Transfer vs fallback vs shortfall" testId="transfer-explainer">
          <p><b>Transfer</b> — you moved it and recorded it here. Two ledger rows: −from, +to. Total stock unchanged.</p>
          <p><b>Fallback</b> — an order needed more than the preferred location had, so the system consumed the rest from the next location automatically. Recorded as usage from that location; nothing was carried.</p>
          <p><b>Shortfall</b> — no location had stock. The sale still went through and the preferred balance went negative by the missing amount, so the next stock take can reconcile it.</p>
        </ExplainerBox>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const SettingsView = ({ http, authHeaders, locData, locations, activeLocations, onChanged }) => {
  const settings = locData?.settings || {};
  const [saving, setSaving] = useState("");
  const [newLoc, setNewLoc] = useState({ name: "", kind: "storage" });
  const [priority, setPriority] = useState([]);
  useEffect(() => { setPriority(locData?.default_priority || []); }, [locData]);

  const patchSettings = async (body) => {
    setSaving("settings");
    try {
      await http.put("/owner/stock-settings", body, { headers: authHeaders });
      toast.success("Settings saved");
      await onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save settings."));
    } finally {
      setSaving("");
    }
  };
  const createLoc = async (e) => {
    e?.preventDefault?.();
    if (!newLoc.name.trim()) { toast.error("Give the location a name."); return; }
    setSaving("create");
    try {
      await http.post("/owner/stock-locations", { name: newLoc.name.trim(), kind: newLoc.kind }, { headers: authHeaders });
      toast.success(`Location "${newLoc.name.trim()}" added`);
      setNewLoc({ name: "", kind: "storage" });
      await onChanged();
    } catch (e2) {
      toast.error(errMsg(e2, "Couldn't create location."));
    } finally {
      setSaving("");
    }
  };
  const patchLoc = async (loc, body) => {
    setSaving(`loc-${loc.id}`);
    try {
      await http.patch(`/owner/stock-locations/${loc.id}`, body, { headers: authHeaders });
      await onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't update location."));
    } finally {
      setSaving("");
    }
  };
  const move = (idx, dir) => {
    setPriority((p) => {
      const n = [...p];
      const j = idx + dir;
      if (j < 0 || j >= n.length) return p;
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  };
  const priorityDirty = JSON.stringify(priority) !== JSON.stringify(locData?.default_priority || []);

  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid="settings-view">
      <Card className="rounded-2xl border-border bg-card">
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Fallback rules</p>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2">
            <div>
              <p className="text-xs font-medium">Automatic fallback</p>
              <p className="text-[11px] text-muted-foreground">When the preferred location can't cover an order, take the rest from the next location in the consumption order.</p>
            </div>
            <Switch checked={settings.fallback_enabled !== false} onCheckedChange={(v) => patchSettings({ fallback_enabled: v })} disabled={saving === "settings"} data-testid="settings-fallback-enabled" />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2">
            <div>
              <p className="text-xs font-medium inline-flex items-center gap-1">{settings.fallback_notifications !== false ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />} Fallback notifications</p>
              <p className="text-[11px] text-muted-foreground">Flag the first fallback of each episode in the event feed and dashboard banner. Shortfalls are always flagged.</p>
            </div>
            <Switch checked={settings.fallback_notifications !== false} onCheckedChange={(v) => patchSettings({ fallback_notifications: v })} disabled={saving === "settings"} data-testid="settings-fallback-notifications" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Default receiving location</Label>
            <Select value={settings.default_receiving_location_id || "__none"} onValueChange={(v) => patchSettings({ default_receiving_location_id: v === "__none" ? "" : v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="settings-default-receiving"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Back of house (default)</SelectItem>
                {activeLocations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Consumption order (business default)</Label>
            <p className="text-[11px] text-muted-foreground">Orders consume from the first location; each next one is a fallback. Individual ingredients can override this from the Overview.</p>
            <div className="space-y-1" data-testid="settings-priority-list">
              {priority.map((id, idx) => {
                const l = locations.find((x) => x.id === id);
                if (!l) return null;
                return (
                  <div key={id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-1.5 text-xs" data-testid={`settings-priority-${id}`}>
                    <span><Badge variant={idx === 0 ? "default" : "outline"} className="mr-2 rounded-full text-[10px]">{idx === 0 ? "preferred" : `fallback ${idx}`}</Badge>{l.name}</span>
                    <span className="flex gap-1">
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-lg" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`settings-priority-up-${id}`}><ChevronUp className="h-4 w-4" /></Button>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-lg" disabled={idx === priority.length - 1} onClick={() => move(idx, 1)} data-testid={`settings-priority-down-${id}`}><ChevronDown className="h-4 w-4" /></Button>
                    </span>
                  </div>
                );
              })}
            </div>
            {priorityDirty ? <Button type="button" size="sm" className="rounded-xl" disabled={saving === "settings"} onClick={() => patchSettings({ consumption_priority: priority })} data-testid="settings-priority-save">Save order</Button> : null}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border bg-card">
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Stock locations</p>
          <div className="space-y-1.5" data-testid="settings-location-list">
            {locations.map((l) => (
              <div key={l.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-xs ${l.active === false ? "opacity-60" : "bg-secondary/40"}`} data-testid={`settings-location-${l.id}`}>
                <span>
                  <b>{l.name}</b> <span className="text-muted-foreground">· {humanize(l.kind)}</span>
                  {l.key ? <Badge variant="outline" className="ml-1 rounded-full text-[9px]">{l.key === "floor" ? "legacy floor" : "legacy BOH"}</Badge> : null}
                  {l.active === false ? <Badge variant="outline" className="ml-1 rounded-full text-[9px]">inactive</Badge> : null}
                </span>
                <span className="flex items-center gap-1">
                  <Button type="button" size="sm" variant="ghost" className="h-7 rounded-lg text-xs" disabled={saving === `loc-${l.id}`} onClick={() => { const nm = window.prompt("Rename location", l.name); if (nm && nm.trim() && nm.trim() !== l.name) patchLoc(l, { name: nm.trim() }); }} data-testid={`settings-location-rename-${l.id}`}>Rename</Button>
                  {l.key !== "floor" ? <Button type="button" size="sm" variant="ghost" className="h-7 rounded-lg text-xs" disabled={saving === `loc-${l.id}`} onClick={() => patchLoc(l, { active: l.active === false })} data-testid={`settings-location-toggle-${l.id}`}>{l.active === false ? "Reactivate" : "Deactivate"}</Button> : null}
                </span>
              </div>
            ))}
          </div>
          <form className="grid gap-2 sm:grid-cols-[1fr_150px_auto]" onSubmit={createLoc} data-testid="settings-create-location">
            <Input value={newLoc.name} onChange={(e) => setNewLoc((n) => ({ ...n, name: e.target.value }))} placeholder="New location, e.g. Bar store" className="h-11 rounded-xl" data-testid="settings-new-location-name" />
            <Select value={newLoc.kind} onValueChange={(v) => setNewLoc((n) => ({ ...n, kind: v }))}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="settings-new-location-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{humanize(k)}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="submit" className="h-11 rounded-xl" disabled={saving === "create"} data-testid="settings-new-location-submit">{saving === "create" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Add</Button>
          </form>
          <p className="text-[11px] text-muted-foreground">A location must be empty before it can be deactivated — transfer its stock out first. Nothing is ever deleted, so history stays intact.</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
const IngredientLocationDialog = ({ http, authHeaders, row, locations, activeLocations, defaultPriority, onClose, onSaved }) => {
  const [priority, setPriority] = useState([]);
  const [useDefault, setUseDefault] = useState(true);
  const [levels, setLevels] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!row) return;
    setUseDefault(!row.priority_is_override);
    setPriority(row.priority || defaultPriority);
    const lv = {};
    for (const l of locations) {
      const c = row.locations?.[l.id]?.levels || {};
      lv[l.id] = { min: c.min ?? "", par: c.par ?? "", max: c.max ?? "" };
    }
    setLevels(lv);
  }, [row, locations, defaultPriority]);
  if (!row) return null;
  const move = (idx, dir) => setPriority((p) => { const n = [...p]; const j = idx + dir; if (j < 0 || j >= n.length) return p; [n[idx], n[j]] = [n[j], n[idx]]; return n; });
  const save = async () => {
    setSaving(true);
    try {
      const location_levels = {};
      for (const [lid, v] of Object.entries(levels)) {
        location_levels[lid] = { min: v.min === "" ? null : Number(v.min), par: v.par === "" ? null : Number(v.par), max: v.max === "" ? null : Number(v.max) };
      }
      const body = useDefault ? { clear_priority: true, location_levels } : { consumption_priority: priority, location_levels };
      await http.put(`/owner/ingredients/${row.id}/locations`, body, { headers: authHeaders });
      toast.success(`${row.name} updated`);
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="ingredient-location-dialog">
        <DialogHeader>
          <DialogTitle>{row.name} · locations</DialogTitle>
          <DialogDescription>Where orders consume this ingredient first, and the min / par levels that drive replenishment suggestions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2">
            <div>
              <p className="text-xs font-medium">Use the business default order</p>
              <p className="text-[11px] text-muted-foreground">{(defaultPriority || []).map((id) => locations.find((l) => l.id === id)?.name || "?").join(" → ")}</p>
            </div>
            <Switch checked={useDefault} onCheckedChange={setUseDefault} data-testid="ingredient-priority-use-default" />
          </div>
          {!useDefault ? (
            <div className="space-y-1" data-testid="ingredient-priority-list">
              {priority.map((id, idx) => {
                const l = locations.find((x) => x.id === id);
                if (!l) return null;
                return (
                  <div key={id} className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-1.5 text-xs">
                    <span><Badge variant={idx === 0 ? "default" : "outline"} className="mr-2 rounded-full text-[10px]">{idx === 0 ? "preferred" : `fallback ${idx}`}</Badge>{l.name}</span>
                    <span className="flex gap-1">
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-lg" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`ingredient-priority-up-${id}`}><ChevronUp className="h-4 w-4" /></Button>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 rounded-lg" disabled={idx === priority.length - 1} onClick={() => move(idx, 1)} data-testid={`ingredient-priority-down-${id}`}><ChevronDown className="h-4 w-4" /></Button>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="space-y-2">
            <p className="text-xs font-medium">Levels per location ({row.unit})</p>
            {activeLocations.map((l) => (
              <div key={l.id} className="grid grid-cols-[1fr_80px_80px_80px] items-center gap-2 text-xs" data-testid={`ingredient-levels-${l.id}`}>
                <span className="truncate">{l.name}<span className="block text-[10px] text-muted-foreground">now {fmtQty(row.locations?.[l.id]?.balance)}</span></span>
                {["min", "par", "max"].map((k) => (
                  <Input key={k} type="number" min="0" step="any" placeholder={k} value={levels[l.id]?.[k] ?? ""} onChange={(e) => setLevels((lv) => ({ ...lv, [l.id]: { ...lv[l.id], [k]: e.target.value } }))} className="h-9 rounded-lg" data-testid={`ingredient-level-${l.id}-${k}`} />
                ))}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">min ≤ par ≤ max. Leave blank to skip. Below min → a replenishment suggestion up to par.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={onClose} data-testid="ingredient-location-cancel">Cancel</Button>
            <Button type="button" className="rounded-xl" onClick={save} disabled={saving} data-testid="ingredient-location-save">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ─────────────────────────────────────────────────────────────────────────
// Session 40 — Sync: what this device still has to send, what the server
// applied, and the exceptions a manager must resolve.
// Session 41.5 — no longer a Stock Locations tab; rendered by the header
// DeviceSyncPill's dialog (exported below).
const EVENT_LABEL = {
  order_created: "Sale", order_voided: "Void", stock_waste_recorded: "Waste", stock_received: "Receiving",
  stock_adjusted: "Adjustment", stock_transferred: "Transfer", stock_take_applied: "Stock take",
};
const QUEUE_TONE = { LOCAL: "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300", PENDING: "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300", SYNCED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", FAILED: "border-destructive/50 bg-destructive/10 text-destructive" };

const describeQueued = (row) => {
  const p = row.payload || {};
  const t = String(row.event_type || "");
  if (t === "order_created") return `${(p.order?.items || []).reduce((n, it) => n + (Number(it.quantity) || 0), 0)} item(s) · table ${p.order?.table_number || "?"}`;
  if (t === "order_voided") return `${p.line_indexes?.length ? `${p.line_indexes.length} line(s)` : "whole order"} · ${p.reason || "no reason"}`;
  if (t === "stock_waste_recorded") return `${fmtQty(p.quantity)} ${p.unit || ""} · ${p.reason}`;
  if (t === "stock_received") return `${fmtQty(p.quantity)} ${p.unit || ""}${p.supplier ? ` · ${p.supplier}` : ""}`;
  if (t === "stock_adjusted") return `${p.mode} ${fmtQty(p.quantity)}`;
  if (t === "stock_transferred") return `${(p.lines || []).length} line(s)`;
  if (t === "stock_take_applied") return `${(p.lines || []).length} counted line(s)${p.scope ? ` · ${p.scope}` : ""}`;
  return "";
};

export const SyncView = ({ http, authHeaders, canManage, onStockChanged }) => {
  const [queue, setQueue] = useState({ rows: [], counts: { local: 0, pending: 0, synced: 0, failed: 0 } });
  const [server, setServer] = useState(null);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [resolveDlg, setResolveDlg] = useState(null);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);

  const load = useCallback(async (st) => {
    setLoading(true);
    setError("");
    try { setQueue(await listStockQueue()); } catch (_) { /* IDB unavailable */ }
    try {
      const r = await http.get("/owner/inventory/sync-exceptions", { headers: authHeaders, params: { status: st || status } });
      setServer(r.data);
    } catch (e) {
      if (navigator.onLine === false) setServer(null); else setError(errMsg(e, "Unable to load sync exceptions."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, status]);

  useEffect(() => {
    load(status);
    const refresh = () => load(status);
    const on = () => { setOnline(true); refresh(); };
    const off = () => setOnline(false);
    window.addEventListener(EVENT_SYNC_FLUSHED, refresh);
    window.addEventListener(STOCK_ACTION_QUEUED, refresh);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener(EVENT_SYNC_FLUSHED, refresh);
      window.removeEventListener(STOCK_ACTION_QUEUED, refresh);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const syncNow = async () => {
    setBusy("drain");
    try { await drainNow(); } catch (_) { /* noop */ }
    await load(status);
    if (onStockChanged) await onStockChanged();
    setBusy("");
  };
  const retryLocal = async (row) => { setBusy(row.id); await retryQueuedEvent(row.id); await syncNow(); };
  const discardLocal = (row) => setResolveDlg({ action: "discard_local", row, notes: "" });
  const resolve = async () => {
    if (!resolveDlg) return;
    setBusy("resolve");
    if (resolveDlg.action === "discard_local") {
      // Manager decision — audited on the server BEFORE the local row is dropped
      // so nothing ever disappears silently. Requires connectivity.
      const row = resolveDlg.row;
      try {
        let deviceId = null;
        try { deviceId = getDeviceId(); } catch (_) { /* noop */ }
        await http.post("/owner/inventory/sync-queue/discard", { change_id: row.id, event_type: row.event_type, notes: resolveDlg.notes || null, device_id: deviceId, server_error: row._meta?.server_error ? String(row._meta.server_error).slice(0, 500) : null }, { headers: authHeaders });
        await discardQueuedEvent(row.id);
        toast.success("Queued event discarded and audited");
        setResolveDlg(null);
        await load(status);
      } catch (e) {
        toast.error(errMsg(e, "Couldn't discard — the server must record this decision first."));
      } finally {
        setBusy("");
      }
      return;
    }
    try {
      const r = await http.post(`/owner/inventory/sync-exceptions/${resolveDlg.exc.id}/resolve`, { action: resolveDlg.action, notes: resolveDlg.notes || null, void_password: resolveDlg.void_password || null }, { headers: authHeaders });
      const out = r.data?.outcome?.status || r.data?.retry?.status;
      if (r.data?.exception?.status === "open") toast.warning(`Still not applicable: ${r.data?.retry?.reason || r.data?.retry?.error || "see details"}`);
      else toast.success(out === "discarded" ? "Exception discarded" : out === "applied_override" ? "Physical count applied" : "Event applied");
      setResolveDlg(null);
      await load(status);
      if (onStockChanged) await onStockChanged();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't resolve."));
    } finally {
      setBusy("");
    }
  };

  const pendingCount = queue.counts.local + queue.counts.pending;
  const exceptions = server?.exceptions || [];
  return (
    <div className="space-y-3" data-testid="sync-view">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Connection" value={online ? "Online" : "Offline"} hint={online ? "queued events drain automatically" : "recording locally — nothing is lost"} tone={online ? "good" : "warn"} testId="sync-connection" />
        <StatTile label="Waiting to sync (this device)" value={pendingCount} hint={`${queue.counts.local} new · ${queue.counts.pending} retrying`} tone={pendingCount ? "warn" : "good"} testId="sync-pending" />
        <StatTile label="Rejected on this device" value={queue.counts.failed} hint="permanently refused by the server" tone={queue.counts.failed ? "bad" : "good"} testId="sync-failed" />
        <StatTile label="Open exceptions (server)" value={server?.counts?.open || 0} hint={server ? `${server.offline_events_applied || 0} offline events applied so far` : "server unreachable"} tone={server?.counts?.open ? "bad" : "good"} testId="sync-exceptions-count" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" className="rounded-xl" onClick={syncNow} disabled={busy === "drain" || !online} data-testid="sync-now"><RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy === "drain" ? "animate-spin" : ""}`} />Sync now</Button>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="h-8 rounded-lg">
            {[["open", "Open"], ["resolved", "Resolved"], ["discarded", "Discarded"], ["all", "All"]].map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-md text-xs" data-testid={`sync-status-${k}`}>{l}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>
      {error ? <ErrorState message={error} onRetry={() => load(status)} testId="sync-error" /> : null}

      <Card className="rounded-2xl border-border bg-card" data-testid="sync-local-queue">
        <CardContent className="p-4">
          <p className="text-sm font-semibold">This device's stock queue</p>
          <p className="text-xs text-muted-foreground">Every stock-affecting action is written here first (IndexedDB, survives restart) with its own event id, then sent to the server. The server applies each id once.</p>
          {queue.rows.length ? (
            <div className="mt-3 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="hidden md:table-cell">Detail</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.rows.slice(0, 100).map((row) => {
                    const st = String(row._meta?.status || "");
                    return (
                      <TableRow key={row.id} data-testid={`sync-queue-row-${row.id}`}>
                        <TableCell className="text-xs tabular-nums">{fmtWhen(row.ts)}</TableCell>
                        <TableCell className="text-xs font-medium">{EVENT_LABEL[row.event_type] || humanize(row.event_type)}</TableCell>
                        <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{describeQueued(row)}{row._meta?.server_error ? <span className="block text-destructive">{String(row._meta.server_error)}</span> : row._meta?.deferred_reason && st === "PENDING" ? <span className="block">waiting: {row._meta.deferred_reason}</span> : null}</TableCell>
                        <TableCell><Badge variant="outline" className={`rounded-full text-[10px] ${QUEUE_TONE[st] || ""}`}>{st === "LOCAL" ? "Queued" : st === "PENDING" ? "Sending" : st === "SYNCED" ? "Synced" : st === "FAILED" ? "Rejected" : st}</Badge></TableCell>
                        <TableCell className="text-right">
                          {st === "FAILED" ? (
                            <div className="flex justify-end gap-1">
                              <Button type="button" size="sm" variant="outline" className="h-7 rounded-lg text-xs" disabled={busy === row.id || !online} onClick={() => retryLocal(row)} data-testid={`sync-retry-${row.id}`}>Retry</Button>
                              {canManage ? <Button type="button" size="sm" variant="ghost" className="h-7 rounded-lg text-xs" disabled={busy === row.id || !online} onClick={() => discardLocal(row)} data-testid={`sync-discard-${row.id}`}>Discard</Button> : null}
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : <div className="mt-3"><EmptyState title="Queue is empty" body="Everything this device recorded has reached the server." testId="sync-queue-empty" /></div>}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border bg-card" data-testid="sync-exceptions">
        <CardContent className="p-4">
          <p className="text-sm font-semibold">Sync exceptions (server)</p>
          <p className="text-xs text-muted-foreground">Queued events the server could not apply safely. Nothing here was discarded automatically — resolve each one.</p>
          {loading && !server ? <Skeleton className="mt-3 h-24 w-full rounded-xl" /> : exceptions.length ? (
            <div className="mt-3 space-y-2">
              {exceptions.map((ex) => {
                const line = ex.details?.line;
                return (
                  <div key={ex.id} className="rounded-2xl border border-border bg-secondary/30 p-3 text-xs" data-testid={`sync-exception-${ex.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold">{EVENT_LABEL[ex.event_type] || humanize(ex.event_type)} · <span className="font-normal text-muted-foreground">{humanize(ex.code)}</span> <Badge variant="outline" className={`ml-1 rounded-full text-[9px] ${ex.status === "open" ? "border-destructive/50 text-destructive" : ""}`}>{ex.status}</Badge></p>
                        <p className="mt-1">{ex.message}</p>
                        {line ? (
                          <p className="mt-1 text-muted-foreground" data-testid={`sync-exception-quantities-${ex.id}`}>
                            Device expected <b className="text-foreground">{fmtQty(line.device_expected)}</b> · physically counted <b className="text-foreground">{fmtQty(line.actual)}</b> · server now <b className="text-foreground">{fmtQty(line.server_balance)}</b> {line.unit}
                            {line.recent_movements?.length ? ` · since then: ${line.recent_movements.slice(0, 4).map((m) => `${humanize(m.reason)}${m.consumption_type && m.consumption_type !== "normal" ? ` (${m.consumption_type})` : ""} ${m.difference > 0 ? "+" : ""}${fmtQty(m.difference)}`).join(", ")}` : ""}
                          </p>
                        ) : null}
                        <p className="mt-1 text-[10px] text-muted-foreground">{fmtWhen(ex.created_at)} · {ex.user_name || (ex.event?.role ? `${humanize(ex.event.role)} (business login)` : "unknown user")} · device {ex.device_id || "?"} · {ex.change_id}{ex.resolved_by ? ` · resolved by ${ex.resolved_by} (${ex.resolution})` : ""}</p>
                      </div>
                      {ex.status === "open" && canManage ? (
                        <div className="flex flex-wrap gap-1">
                          {(ex.code === "stock_take_moved" || ex.code === "balance_moved") ? <Button type="button" size="sm" className="h-7 rounded-lg text-xs" onClick={() => setResolveDlg({ exc: ex, action: "apply_override", notes: "" })} data-testid={`sync-exception-override-${ex.id}`}>Apply count anyway</Button> : null}
                          <Button type="button" size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => setResolveDlg({ exc: ex, action: "retry", notes: "", void_password: "" })} data-testid={`sync-exception-retry-${ex.id}`}>Retry</Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => setResolveDlg({ exc: ex, action: "discard", notes: "" })} data-testid={`sync-exception-discard-${ex.id}`}>Discard</Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <div className="mt-3"><EmptyState title={status === "open" ? "No open exceptions" : "Nothing here"} body="Every synchronised event was applied." testId="sync-exceptions-empty" /></div>}
        </CardContent>
      </Card>

      <Dialog open={!!resolveDlg} onOpenChange={(o) => { if (!o) setResolveDlg(null); }}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="sync-resolve-dialog">
          <DialogHeader>
            <DialogTitle>{resolveDlg?.action === "apply_override" ? "Apply the physical count anyway" : resolveDlg?.action === "retry" ? "Retry this event" : resolveDlg?.action === "discard_local" ? "Discard this queued event from this device" : "Discard this event"}</DialogTitle>
            <DialogDescription>
              {resolveDlg?.action === "apply_override" ? "The location balance moved after the count began. Applying sets it to the counted value and overrides the newer balance — only do this if you trust the count." : resolveDlg?.action === "retry" ? "Re-run the event as recorded (after fixing the cause). For a void you can supply the void password here." : resolveDlg?.action === "discard_local" ? "Manager decision: the action will never reach stock. The discard is recorded in the audit log and any matching server exception is closed as discarded." : "The event stays in history as discarded. Stock will not change."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {resolveDlg?.action === "discard_local" && resolveDlg?.row ? (
              <div className="rounded-xl border border-border bg-secondary/40 p-2 text-xs" data-testid="sync-discard-local-summary">
                <p className="font-medium">{EVENT_LABEL[resolveDlg.row.event_type] || humanize(resolveDlg.row.event_type)} · {describeQueued(resolveDlg.row)}</p>
                {resolveDlg.row._meta?.server_error ? <p className="mt-0.5 text-destructive">Server said: {String(resolveDlg.row._meta.server_error)}</p> : null}
                <p className="mt-0.5 text-muted-foreground">{resolveDlg.row.id}</p>
              </div>
            ) : null}
            {resolveDlg?.action === "retry" && resolveDlg?.exc?.event_type === "order_voided" ? (
              <div className="space-y-1.5"><Label className="text-xs">Void password</Label><Input type="password" value={resolveDlg.void_password || ""} onChange={(e) => setResolveDlg((d) => ({ ...d, void_password: e.target.value }))} className="h-11 rounded-xl" data-testid="sync-resolve-void-password" /></div>
            ) : null}
            <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={resolveDlg?.notes || ""} onChange={(e) => setResolveDlg((d) => ({ ...d, notes: e.target.value }))} className="rounded-xl" data-testid="sync-resolve-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setResolveDlg(null)} data-testid="sync-resolve-cancel">Cancel</Button>
              <Button type="button" className="rounded-xl" variant={resolveDlg?.action === "discard" || resolveDlg?.action === "discard_local" ? "destructive" : "default"} disabled={busy === "resolve"} onClick={resolve} data-testid="sync-resolve-confirm">{busy === "resolve" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
